import React from 'react';
import { Phone, Mail, MapPin, Clock, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from './shared/SEO';
import ContactForm from './forms/ContactForm';
import PageLayout from './layout/PageLayout';

const ContactPage = () => {
  const contactSchema = {
    "@context": "http://schema.org",
    "@type": "Organization",
    "name": "COTAD",
    "url": "http://www.dragoncoven.org",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-781-366-8800",
      "contactType": "Support Line"
    }
  };

  const handleSubmit = async (formData) => {
    const response = await fetch('/api/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to submit form');
    }
  };

  const contactInfo = [
    { 
      icon: Heart, 
      title: "24/7 Support Line", 
      content: "781-366-8800", 
      gradient: "from-red-500 to-pink-500", 
      link: "tel:781-366-8800" 
    },
    { 
      icon: Phone, 
      title: "Office Phone", 
      content: "978-564-5221", 
      gradient: "from-blue-600 to-cyan-500", 
      link: "tel:978-564-5221" 
    },
    { 
      icon: Mail, 
      title: "Email", 
      content: "cotad@twilightmail.org", 
      gradient: "from-purple-600 to-blue-600", 
      link: "mailto:cotad@twilightmail.org" 
    },
    { 
      icon: MapPin, 
      title: "Location", 
      content: "130 Western Ave, Lowell, MA", 
      gradient: "from-cyan-500 to-green-500" 
    },
    { 
      icon: Clock, 
      title: "Circle Services", 
      content: "Wednesday & Sunday", 
      gradient: "from-orange-500 to-amber-500" 
    }
  ];

  return (
    <>
      <SEO
        title="Contact COTAD | 24/7 Youth Support Services"
        description="Need support? Contact COTAD for 24/7 assistance and counseling. Our support line is always open at (781) 366-8800."
        pathname="/contact"
      >
        <script type="application/ld+json">
          {JSON.stringify(contactSchema)}
        </script>
      </SEO>

      <PageLayout
        title="Contact Us"
        description="We're here to listen and support you, day or night"
      >
        <section className="contact-content-section">
          <div className="contact-content-container">
            <div className="contact-content-grid">
              {/* Contact Information */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="contact-info-card"
              >
                <h2 className="contact-info-title">Reach Out to Us</h2>
                
                <div className="space-y-6">
                  {contactInfo.map((info, index) => (
                    <motion.div 
                      key={index}
                      className="contact-info-item"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8, delay: 0.4 + (index * 0.1) }}
                    >
                      <a href={info.link} className={`contact-info-icon bg-gradient-to-r ${info.gradient}`}>
                        <info.icon className="w-full h-full text-white" />
                      </a>
                      <div>
                        <p className="contact-info-text">{info.title}</p>
                        <a href={info.link} className="contact-info-link">{info.content}</a>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="contact-form-card"
              >
                <ContactForm onSubmit={handleSubmit} />
              </motion.div>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
};

export default ContactPage;